import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, colors } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Popper } from "Components/_Shared/Popper";
import { type InsuranceContractFormType, getInsuranceContractFormSchema } from "Forms/Schemas/Insurance/insuranceContractFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getListInsuranceContractsQueryKey,
  useCreateInsuranceContract,
  useDeleteInsuranceContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateInsuranceContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Contract, ContractContractType, ListAnalyzerPartnersToolType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  readonly onClose: () => void;
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly insuranceContractType: ContractContractType;
  readonly insuranceContract?: Contract;
  readonly open: boolean;
};

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: ${colors.backgroundSecondary};
  }
`;

export const InsuranceContractDrawer: FC<Props> = ({
  onClose: propsOnClose,
  analysisId,
  versionId,
  clientId,
  insuranceContract,
  insuranceContractType,
  open,
}) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();

  const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

  const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });
  const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);

  const { mutateAsync: createInsuranceContract } = useCreateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractCreated));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const { mutateAsync: updateInsuranceContract } = useUpdateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractEdited));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const { mutateAsync: deleteInsuranceContract } = useDeleteInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractDeleted));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const { control, handleSubmit, setValue, reset } = useForm<InsuranceContractFormType>({
    resolver: yupResolver(getInsuranceContractFormSchema(t)),
    mode: "onBlur",
  });

  const onClose = () => {
    propsOnClose();
    setExpenseOptionsDropdownOpen(false);
  };

  const onSubmit = async (data: InsuranceContractFormType) => {
    try {
      const payload = {
        partnerId: data.partnerId ? Number(data.partnerId) : null,
        createRecommended: !!insuranceContract?.createRecommended,
        insuranceAmount: {
          currencyCode: "CZK",
          frequency: data.frequency as AmountFrequency,
          value: data.insuranceAmount,
        },
        contractType: insuranceContract ? insuranceContract.contractType : insuranceContractType,
        note: data.note || "",
        product: data.productName || "",
      };

      if (!insuranceContract) {
        await createInsuranceContract({
          analysisId,
          versionId,
          clientGuid: clientId,
          data: payload,
        });
      } else {
        await updateInsuranceContract({
          analysisId,
          versionId,
          clientGuid: clientId,
          contractGuid: insuranceContract.guid,
          data: payload,
        });
      }
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const onDelete = async () => {
    if (!insuranceContract) return;
    try {
      await deleteInsuranceContract({ analysisId, versionId, clientGuid: clientId, contractGuid: insuranceContract.guid });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const insurancePartners: Option[] =
    insurancePartnersData?.map((partner) => ({
      value: partner.id.toString(),
      name: partner.name,
    })) || [];

  const frequencies: Option[] = [
    { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
    { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
    { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
    { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
    { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
  ];

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    const defaultValues = {
      partnerId: insuranceContract?.partnerId?.toString(),
      frequency: insuranceContract?.insuranceAmount.frequency ?? AmountFrequency.monthly,
      insuranceAmount: insuranceContract?.insuranceAmount.value.toString() ?? ("0" as any),
      note: insuranceContract?.note,
      productName: insuranceContract?.product,
    };
    reset(defaultValues);
  }, [insuranceContract, open, reset]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(Resources.Common.Close)}
      title={
        insuranceContractType === ContractContractType.current
          ? t(Resources.AnalysisDetail.Insurance.ExistingSolution)
          : t(Resources.AnalysisDetail.Insurance.RecommendedSolution)
      }
      startAction={
        !!insuranceContract && (
          <Button variant="text" color="error" disabled={readOnly} onClick={onDelete}>
            {t(Resources.Common.Delete)}
          </Button>
        )
      }
      primaryAction={
        <Button variant="contained" disabled={readOnly} type="submit" onClick={() => handleSubmit(onSubmit)()}>
          {insuranceContract ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
        </Button>
      }
    >
      <Stack spacing={Spacing.XL} py={Spacing.XXL}>
        <Grid container spacing={Spacing.XL}>
          {expenseOptions && expenseOptions.length > 0 && (
            <Grid size={12}>
              <Popper
                placement="bottom-start"
                isOpen={expenseOptionsDropdownOpen}
                onOpen={() => {
                  setExpenseOptionsDropdownOpen(true);
                }}
                btnEl={
                  <Button
                    variant="text"
                    disabled={readOnly}
                    sx={{ width: "fit-content", marginLeft: -Spacing.M }}
                    endIcon={<ChevronDownIcon sx={{ color: "primary.main" }} />}
                  >
                    {t(Resources.AnalysisDetail.Insurance.LoadFromExistingExpenses)}
                  </Button>
                }
              >
                <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                  {expenseOptions?.map((option, index) => (
                    <StyledItem
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingX: Spacing.M,
                        paddingY: Spacing.S,
                        borderRadius: Radius.S,
                      }}
                      key={`${option.expenseType}-${index}`}
                      onClick={() => {
                        setValue("insuranceAmount", option.amount?.value.toString() as any);
                        if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                        setExpenseOptionsDropdownOpen(false);
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {t(
                            Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes[
                              LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY[option.expenseType]
                            ],
                          )}
                        </Typography>
                        <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                          {option.institution}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="secondary" fontWeight={500}>
                        {formatCurrency(option.amount?.value ?? 0, option.amount?.currencyCode ?? "CZK")}/
                        {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                      </Typography>
                    </StyledItem>
                  ))}
                </Stack>
              </Popper>
            </Grid>
          )}
          <Grid size={6}>
            <FormSelect
              control={control}
              name="partnerId"
              label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
              required
              options={toSelectOptions(insurancePartners)}
              placeholder={t(Resources.Common.SelectPlaceholder)}
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <FormTextField
              control={control}
              name="productName"
              label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Product)}
              required
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <FormNumberField
              control={control}
              name="insuranceAmount"
              label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
              valueType="text"
              scale={0}
              min={0}
              thousandsSeparator=" "
              unit={t(Resources.Common.CZKSuffix)}
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <FormSelect
              control={control}
              name="frequency"
              label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
              options={toSelectOptions(frequencies)}
              placeholder={t(Resources.Common.SelectPlaceholder)}
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FormTextField
              disabled={readOnly}
              control={control}
              name="note"
              label={t(Resources.AnalysisDetail.Goals.GoalNote)}
              multiline
              minRows={6}
              fullWidth
            />
          </Grid>
        </Grid>
      </Stack>
    </Drawer>
  );
};
